import { useState } from 'react';
import { Link } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import {
    Divider, IconButton, ListItemIcon, ListItemText,
    Menu, MenuItem, Tooltip,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';

// ─── Semantic colour tokens ───────────────────────────────────────────────────

const COLOR_MAP = {
    error:   { text: '#dc2626', hover: '#fef2f2' },
    warning: { text: '#d97706', hover: '#fffbeb' },
    success: { text: '#16a34a', hover: '#f0fdf4' },
    primary: { text: '#2563eb', hover: '#eff6ff' },
};

// ─── Styled components ────────────────────────────────────────────────────────

const TriggerButton = styled(IconButton)({
    borderRadius: '6px',
    padding: '4px',
    backgroundColor: '#e3f2fd',
    color: '#1565c0',
    transition: 'background 0.15s, color 0.15s',
    '& svg': { fontSize: '16px !important' },
    '&:hover': { backgroundColor: '#1565c0', color: '#fff' },
    '&[aria-expanded="true"]': { backgroundColor: '#1565c0', color: '#fff' },
});

const ActionMenu = styled(Menu)({
    '& .MuiPaper-root': {
        minWidth: 190,
        marginTop: '4px',
        padding: '3px',
        borderRadius: '8px',
        border: '1px solid #e2e8f0',
        boxShadow: '0 8px 24px rgba(15,23,42,0.12)',
        elevation: 0,
    },
    '& .MuiList-root': { padding: 0 },
});

const ActionDivider = styled(Divider)({
    margin: '2px 0 !important',
    borderColor: '#f1f5f9',
});

// Color-aware menu item — receives `itemcolor` (string key into COLOR_MAP)
// to avoid forwarding non-HTML `color` prop to the DOM element.
const ActionItem = styled(MenuItem, {
    shouldForwardProp: (p) => p !== 'itemcolor',
})(({ itemcolor }) => {
    const c = itemcolor ? COLOR_MAP[itemcolor] : null;
    return {
        borderRadius: '4px',
        paddingLeft: '8px',
        paddingRight: '8px',
        paddingTop: '5px',
        paddingBottom: '5px',
        gap: '2px',
        color: c?.text || 'inherit',
        textDecoration: 'none',
        transition: 'background 0.12s, color 0.12s',
        '&:hover': {
            backgroundColor: c?.hover || '#f8fafc',
            color: c?.text || '#1e293b',
            textDecoration: 'none',
        },
        '&.Mui-disabled': { opacity: 0.45 },
    };
});

const ActionItemIcon = styled(ListItemIcon, {
    shouldForwardProp: (p) => p !== 'itemcolor',
})(({ itemcolor }) => {
    const c = itemcolor ? COLOR_MAP[itemcolor] : null;
    return {
        color: c?.text || '#64748b',
        minWidth: '32px !important',
    };
});

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Generic row-level actions menu (three-dot trigger → styled dropdown).
 *
 * Props
 * ─────
 * actions   Action[]   Required. Ordered list of action config objects.
 * tooltip   string     Tooltip text on the trigger (default: 'Actions').
 *
 * Action shape
 * ─────────────
 * key            string        Unique React key.
 * label          string        Menu item label.
 * icon           ReactNode     Leading icon element.
 * onClick?       () => void    Handler called after the menu closes.
 * href?          string        If set, renders as a <Link to={href}>.
 * target?        string        Link target, e.g. '_blank'.
 * color?         'error' | 'warning' | 'success' | 'primary'
 * disabled?      boolean
 * hidden?        boolean       Skip rendering entirely when true.
 * dividerBefore? boolean       Render a thin Divider above this item.
 */
export default function OrderRowActions({ actions = [], tooltip = 'Actions' }) {
    const [anchor, setAnchor] = useState(null);
    const isOpen = Boolean(anchor);

    const handleOpen = (e) => {
        e.stopPropagation();
        setAnchor(e.currentTarget);
    };
    const handleClose = () => setAnchor(null);

    const visible = actions.filter((a) => !a.hidden);

    // Single action — render directly as an icon button, no menu needed.
    if (visible.length === 1) {
        const a = visible[0];
        const c = a.color ? COLOR_MAP[a.color] : null;
        const linkProps = a.href
            ? { component: Link, to: a.href, target: a.target, rel: a.target === '_blank' ? 'noopener noreferrer' : undefined }
            : {};
        return (
            <Tooltip title={a.label} placement="left">
                <TriggerButton
                    size="small"
                    {...linkProps}
                    onClick={a.onClick}
                    disabled={a.disabled}
                    sx={{
                        bgcolor: c?.hover || '#e3f2fd',
                        color: c?.text || '#1565c0',
                        borderRadius: '6px',
                        padding: '4px',
                        '&:hover': { bgcolor: c?.text || '#1565c0', color: '#fff' },
                        '&.Mui-disabled': { opacity: 0.45 },
                        '& svg': { fontSize: '16px !important' },
                    }}
                >
                    {a.icon}
                </TriggerButton>
            </Tooltip>
        );
    }

    return (
        <>
            <Tooltip title={tooltip} placement="left">
                <TriggerButton size="small" onClick={handleOpen}>
                    <MoreVertIcon sx={{ fontSize: 18 }} />
                </TriggerButton>
            </Tooltip>

            <ActionMenu
                anchorEl={anchor}
                open={isOpen}
                onClose={handleClose}
                onClick={handleClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                elevation={0}
            >
                {visible.map((action) => {
                    const linkProps = action.href
                        ? {
                            component: Link,
                            to: action.href,
                            target: action.target,
                            rel: action.target === '_blank' ? 'noopener noreferrer' : undefined,
                        }
                        : {};

                    return [
                        action.dividerBefore && <ActionDivider key={`divider-${action.key}`} />,
                        <ActionItem
                            key={action.key}
                            itemcolor={action.color}
                            {...linkProps}
                            onClick={action.onClick}
                            disabled={action.disabled}
                        >
                            <ActionItemIcon itemcolor={action.color}>
                                {action.icon}
                            </ActionItemIcon>
                            <ListItemText
                                primary={action.label}
                                primaryTypographyProps={{ fontSize: 12, fontWeight: 500, lineHeight: 1.3 }}
                            />
                        </ActionItem>,
                    ];
                })}
            </ActionMenu>
        </>
    );
}
